import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Summary } from '../../../../types';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@components/ui';
import {
  Download,
  AlertCircle,
  Loader2,
  Grid3x3,
  GitBranch,
  TreePine,
  Clock,
  ChevronDown,
} from 'lucide-react';
import { useMindmap } from '../../../../lib/hooks/use-new-api';
import { cn } from '../../../../lib/utils';
import { useSummarizer } from '@core/store/useSummarizer';
import { getUserFriendlyErrorMessage } from '../../../../lib/error-utils';
import { useToast, toast } from '@components/ui/toast';

import { isAxiosError } from 'axios';

// Lazy load simple-mind-map to reduce initial bundle size
let MindMap: any = null;
let Export: any = null;
let mindMapLoadPromise: Promise<void> | null = null;

// Initialize plugins and theme at module level
let mindMapPluginsInitialized = false;

const loadMindMapLibrary = async () => {
  if (mindMapLoadPromise) return mindMapLoadPromise;

  mindMapLoadPromise = (async () => {
    const [mindMapModule, exportModule] = await Promise.all([
      import(/* webpackChunkName: "mindmap" */ 'simple-mind-map'),
      // @ts-expect-error - No types available for export plugin
      import(/* webpackChunkName: "mindmap-export" */ 'simple-mind-map/src/plugins/Export.js'),
    ]);

    MindMap = mindMapModule.default;
    Export = exportModule.default;
  })();

  return mindMapLoadPromise;
};

// eslint-disable-next-line react-hooks/rules-of-hooks
const setupMindMapPlugins = async () => {
  if (mindMapPluginsInitialized) return;

  await loadMindMapLibrary();

  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    MindMap.usePlugin(Export);

    // Define a dark theme for the mind map
    MindMap.defineTheme('dark', {
      backgroundColor: 'hsl(222.2 84% 4.9%)',
      lineColor: 'rgb(107, 114, 128)',
      lineWidth: 1,
      generalizationLineWidth: 1,
      generalizationLineColor: 'rgb(107, 114, 128)',
      root: {
        fillColor: 'hsl(217.2 32.6% 17.5%)',
        color: 'hsl(210 40% 98%)',
        borderColor: 'rgb(107, 114, 128)',
        borderWidth: 1,
        fontSize: 18,
        fontWeight: 'bold',
      },
      second: {
        fillColor: 'hsl(217.2 32.6% 17.5%)',
        color: 'hsl(210 40% 98%)',
        borderColor: 'rgb(107, 114, 128)',
        borderWidth: 1,
        fontSize: 16,
      },
      node: {
        color: 'hsl(210 40% 98%)',
        fontSize: 14,
      },
      generalization: {
        fillColor: 'hsl(217.2 32.6% 17.5%)',
        color: 'hsl(210 40% 98%)',
        borderColor: 'rgb(107, 114, 128)',
        borderWidth: 1,
      },
    });

    mindMapPluginsInitialized = true;
  } catch (error) {
    // Silently handle plugin initialization errors
  }
};

// Initialize immediately
setupMindMapPlugins();

interface MindmapTabProps {
  summary: Summary;
}

interface MindMapNode {
  data: {
    text: string;
    expand: boolean;
    uid?: string;
  };
  children?: MindMapNode[];
}

interface MindMapData {
  data: {
    text: string;
    expand: boolean;
  };
  children: MindMapNode[];
}

// Layouts available in simple-mind-map
const LAYOUTS = [
  { id: 'logicalStructure', name: 'Logical Structure', icon: TreePine },
  { id: 'mindMap', name: 'Mind Map', icon: GitBranch },
  { id: 'organizationStructure', name: 'Organization', icon: Grid3x3 },
  { id: 'timeline', name: 'Timeline', icon: Clock },
];

// Convert mermaid data to simple-mind-map format
function convertMermaidToMindMap(mermaidText: string): MindMapData {
  const lines = mermaidText.trim().split('\n');
  const nodes: Record<string, string> = {};
  const edges: Array<{ from: string; to: string }> = [];

  // Parse mermaid flowchart
  const startIndex = lines.findIndex(
    (line) => line.trim().startsWith('flowchart') || line.trim().startsWith('graph')
  );

  if (startIndex === -1) {
    return {
      data: { text: 'Mind Map', expand: true },
      children: [],
    };
  }

  // Extract nodes and edges
  for (let i = startIndex + 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line || line.startsWith('%%')) continue;

    // Match edge with label: FROM --> TO["LABEL"]
    const edgeMatch = line.match(/^(\w+)\s*-->\s*(\w+)\["(.+?)"\]/);
    // Match standalone node: NODE["LABEL"]
    const nodeMatch = line.match(/^(\w+)\["(.+?)"\]/);
    // Also match edges without brackets: FROM --> TO
    const simpleEdgeMatch = line.match(/^(\w+)\s*-->\s*(\w+)$/);

    if (edgeMatch) {
      const [, from, to, label] = edgeMatch;
      edges.push({ from, to });
      nodes[to] = label;
      // Also add the 'from' node if not already present
      if (!nodes[from]) {
        nodes[from] = from;
      }
    } else if (nodeMatch) {
      const [, id, label] = nodeMatch;
      nodes[id] = label;
    } else if (simpleEdgeMatch) {
      const [, from, to] = simpleEdgeMatch;
      edges.push({ from, to });
      // Add nodes if not present
      if (!nodes[from]) nodes[from] = from;
      if (!nodes[to]) nodes[to] = to;
    }
  }

  // Build tree structure
  function buildTree(nodeId: string, visited = new Set<string>()): MindMapNode {
    if (visited.has(nodeId)) {
      return {
        data: { text: nodes[nodeId] || nodeId, expand: true, uid: nodeId },
        children: [],
      };
    }

    visited.add(nodeId);
    const children = edges
      .filter((edge) => edge.from === nodeId)
      .map((edge) => buildTree(edge.to, visited));

    return {
      data: { text: nodes[nodeId] || nodeId, expand: true, uid: nodeId },
      children,
    };
  }

  // Find root node (START or first node)
  const rootId = 'START' in nodes ? 'START' : Object.keys(nodes)[0];

  if (!rootId) {
    return {
      data: { text: 'Mind Map', expand: true },
      children: [],
    };
  }

  const tree = buildTree(rootId);

  return {
    data: tree.data,
    children: tree.children || [],
  };
}

export const MindmapTab: React.FC<MindmapTabProps> = ({ summary }) => {
  const { quota, triggerCTADialog } = useSummarizer();
  const { addToast } = useToast();
  const { mindmap, loading, error, generateMindmap } = useMindmap({
    onError: (err) => {
      if (isAxiosError(err)) {
        const data = err.response?.data;
        if (data.cta) {
          triggerCTADialog(data.cta);
          return;
        }
      }

      if (err.code === 'QUOTA_EXCEEDED') {
        triggerCTADialog('quota_exceeded');
      }
    },
  });
  const [mindMapInstance, setMindMapInstance] = useState<any>(null);
  const [hasGeneratedOnce, setHasGeneratedOnce] = useState(false);
  const [mindMapData, setMindMapData] = useState<MindMapData | null>(null);
  const [currentLayout, setCurrentLayout] = useState('organizationStructure');
  const containerRef = useRef<HTMLDivElement>(null);

  // Generate mindmap only once when component mounts or summary changes
  useEffect(() => {
    if (summary.id && !hasGeneratedOnce && !loading) {
      setHasGeneratedOnce(true);
      generateMindmap({
        summary_id: summary.id,
        format: 'mermaid',
        max_depth: 3,
        include_details: true,
        transcript: summary.transcript || '',
        user_id: quota?.user_id,
      });
    }
  }, [summary, hasGeneratedOnce, loading, generateMindmap]);

  // Process mermaid data when available
  useEffect(() => {
    if (mindmap && mindmap.trim()) {
      const convertedData = convertMermaidToMindMap(mindmap);
      setMindMapData(convertedData);
    }
  }, [mindmap, loading, hasGeneratedOnce]);

  // Initialize mind map when data is ready
  useEffect(() => {
    if (mindMapData && containerRef.current && !mindMapInstance) {
      const initMindMap = async () => {
        try {
          // Load MindMap library if not already loaded
          await loadMindMapLibrary();
          await setupMindMapPlugins();

          if (!containerRef.current) return; // Check again after async load

          // Clear any existing content in container
          containerRef.current.innerHTML = '';

          const mm = new MindMap({
            el: containerRef.current,
            data: mindMapData,
            layout: currentLayout,
            theme: 'classic',
            readonly: false,
            fit: true,
            // Add explicit configuration like reference
            isUseCustomNodeWidth: false,
            mousewheelAction: 'zoom',
            mousewheelMoveStep: 100,
            defaultInsertSecondLevelNodeText: 'New Node',
            defaultInsertBelowSecondLevelNodeText: 'New Node',
          } as any);

          // Listen to data changes for real-time updates
          mm.on('data_change', (data: any) => {
            setMindMapData(data);
          });

          setMindMapInstance(mm);

          // Auto-fit content to screen after initialization with longer delay
          setTimeout(() => {
            if (mm?.view) {
              try {
                (mm.view as any).fit();
                mm.setLayout(currentLayout);
              } catch (error) {
                console.error('Error auto-fitting view:', error);
              }
            } else {
              // View not available yet, skip auto-fit
            }
          }, 1000); // Longer delay to ensure rendering is complete
        } catch (err) {
          console.error('Error initializing mind map:', err);
        }
      };

      initMindMap();
    }
  }, [mindMapData, mindMapInstance, currentLayout]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mindMapInstance) {
        try {
          mindMapInstance.destroy();
        } catch (error) {
          console.error('Error destroying mind map:', error);
        }
      }
    };
  }, [mindMapInstance]);

  const handleRetry = () => {
    setHasGeneratedOnce(false); // Allow re-generation
  };

  const handleLayoutChange = useCallback(
    (layoutId: string) => {
      if (mindMapInstance) {
        try {
          mindMapInstance.setLayout(layoutId);
          setCurrentLayout(layoutId);
        } catch (error) {
          console.error('Error changing layout:', error);
        }
      }
    },
    [mindMapInstance]
  );

  const handleExportPNG = useCallback(() => {
    if (!mindMapInstance) {
      return;
    }

    try {
      mindMapInstance.export('png', true, `${summary.title || 'mindmap'}-mindmap`);
    } catch (error) {
      addToast(toast.error('Failed to export mindmap as PNG'));
    }
  }, [mindMapInstance, summary.title, addToast]);

  const handleExportSVG = useCallback(() => {
    if (!mindMapInstance) {
      return;
    }

    try {
      mindMapInstance.export('svg', true, `${summary.title || 'mindmap'}-mindmap`);
    } catch (error) {
      console.error('Error exporting SVG:', error);
      addToast(toast.error('Failed to export mindmap as SVG'));
    }
  }, [mindMapInstance, summary.title, addToast]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-500 mb-4" />
          <p className="text-gray-600">Generating mindmap...</p>
        </div>
      </div>
    );
  }

  if (error || !mindmap) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-red-500 mb-4" />
          <p className="text-gray-600 mb-2">
            {error?.code === 'QUOTA_EXCEEDED' ? 'Quota Exceeded' : 'Failed to generate mindmap'}
          </p>
          <p className="text-sm text-gray-500 mb-4">
            {error
              ? getUserFriendlyErrorMessage({
                  code: error.code,
                  message: error.message,
                  shouldShowCTA: false,
                })
              : 'Mindmap could not be generated for this content'}
          </p>
          <Button onClick={handleRetry} variant="outline">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (!mindMapData) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-yellow-500 mb-4" />
          <p className="text-gray-600">Processing mindmap data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex h-full flex-col')}>
      {/* Compact Toolbar */}
      <div className="flex items-center justify-between border-b bg-gray-50 px-3 py-2 flex-shrink-0">
        {/* Layout Controls */}
        <div className="flex items-center gap-1 rounded-md border p-1 bg-white">
          {LAYOUTS.map((layout) => {
            const IconComponent = layout.icon;
            return (
              <Button
                key={layout.id}
                variant={currentLayout === layout.id ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleLayoutChange(layout.id)}
                disabled={!mindMapInstance}
                className={cn(
                  'h-7 w-7 p-0 border',
                  currentLayout === layout.id
                    ? 'bg-black text-white hover:bg-gray-800'
                    : 'bg-white hover:bg-gray-100'
                )}
                title={layout.name}
              >
                <IconComponent className="h-3 w-3" />
              </Button>
            );
          })}
        </div>

        {/* Action Controls */}
        <div className="items-center gap-2 hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={!mindMapInstance}
                className="h-7 px-2 flex items-center gap-1"
                title="Download Options"
              >
                <Download className="h-3 w-3" />
                <ChevronDown className="h-2 w-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[100px]">
              <DropdownMenuItem onClick={handleExportPNG} className="cursor-pointer">
                PNG
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportSVG} className="cursor-pointer">
                SVG
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mindmap Content */}
      <div className="flex-1 relative overflow-hidden bg-white">
        <div ref={containerRef} className="w-full h-full" style={{ minHeight: '500px' }} />

        {!mindMapInstance && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">Initializing mindmap...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
